import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback } from "react";

import {
  CheckCircleIcon,
  ShoppingBasketIcon,
  ClockIcon,
  ListIcon,
} from "lucide-react";
import {
  DailyStoreTabs,
  type DailyStoreTab,
} from "./common/DailyStoreTabs";
import OrderDetailsModal, { type OrderLikeForDetails } from "./OrderDetailsModal";
import { fetchTodaysDeliveries, pickupOrder } from "../services/today.Service";
import { useLoading } from "../contexts/LoadingContext";
import { useSearch } from "../contexts/SearchContext";
import { useToast } from "../contexts/ToastContext";
import {
  getOrderBasketStatus,
  isOrderConfirmableInBasket,
  isOrderEligibleForBasket,
  sortBasketOrdersForDisplay,
} from "../utils/basketOrderFilter";
import {
  addReleasedOrderIds,
  getReleasedOrderIdsSynced,
} from "../utils/basketReleaseStorage";
import { pickPrimaryImageUrl } from "../utils/pickPrimaryImageUrl";
import { MAIN_LOADER_MIN_HEIGHT } from "./common/MainContentLoader";
import { formatDeliveryAddressOrFallback } from "../utils/formatDeliveryAddress";
import {
  defaultStoreSlotExpandKeys,
  partitionDailyAndStoreOrders,
} from "../utils/deliveryTimeSlots";
import { DeliveryOrderGroupSections } from "./DeliveryOrderGroupSections";

const LS_SELECTED_DAILY = "selectedDailyOrderIds";
const LS_SELECTED_STORE = "selectedStoreOrderIds";

interface MyBasketProps {
  onConfirm: () => void;
  isConfirmed: boolean;
  /** Increment after check-in to reload auto-assigned preparing orders. */
  ordersRefreshKey?: number;
  /** When false, basket confirm is blocked until partner checks in. */
  partnerCheckedIn?: boolean | null;
  onCheckInRequired?: () => void;
}

function loadSelectedIds(key: string): number[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map(Number).filter((n) => Number.isFinite(n));
  } catch {
    return [];
  }
}

function saveSelectedIds(key: string, ids: number[]) {
  localStorage.setItem(key, JSON.stringify(ids));
}

function packQuantityForOrders(orders: { items?: { quantity?: number }[] }[]): number {
  return orders.reduce(
    (sum, o) =>
      sum + (o.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 1),
    0
  );
}

function categoryCountsForOrders(
  orders: { items?: { product?: { category_name?: string } }[] }[]
): Record<string, number> {
  const counts: Record<string, number> = {};
  orders.forEach((order) => {
    const category =
      order.items?.[0]?.product?.category_name || "Puja Flowers";
    counts[category] = (counts[category] || 0) + 1;
  });
  return counts;
}

const MyBasket: React.FC<MyBasketProps> = ({
  onConfirm,
  isConfirmed,
  ordersRefreshKey = 0,
  partnerCheckedIn = null,
  onCheckInRequired,
}) => {
  const [basketTab, setBasketTab] = useState<DailyStoreTab>("daily");
  const [selectedDailyIds, setSelectedDailyIds] = useState<number[]>([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([]);
  const [confirmTimeDaily, setConfirmTimeDaily] = useState("");
  const [confirmTimeStore, setConfirmTimeStore] = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [otherTabReminder, setOtherTabReminder] = useState<{
    tab: DailyStoreTab;
    count: number;
  } | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());
  const [dataReady, setDataReady] = useState(false);
  const [detailsOrder, setDetailsOrder] = useState<OrderLikeForDetails | null>(
    null
  );
  const { setLoading } = useLoading();
  const { searchTerm } = useSearch();
  const { showSuccess, showError } = useToast();

  const selectedIds =
    basketTab === "daily" ? selectedDailyIds : selectedStoreIds;
  const setSelectedIds =
    basketTab === "daily" ? setSelectedDailyIds : setSelectedStoreIds;
  const setConfirmTime =
    basketTab === "daily" ? setConfirmTimeDaily : setConfirmTimeStore;

  useLayoutEffect(() => {
    setLoading(true);
    return () => {
      setLoading(false);
    };
  }, [setLoading]);

  useEffect(() => {
    let cancelled = false;
    const loadOrders = async () => {
      setDataReady(false);
      setLoading(true);
      try {
        const response = await fetchTodaysDeliveries();
        const allOrders = response.orders || [];
        const released = getReleasedOrderIdsSynced(
          allOrders as Array<{ id?: number; status?: string }>
        );
        const readyOrders = allOrders.filter((order: any) =>
          isOrderEligibleForBasket(order as Record<string, unknown>, released)
        );
        if (!cancelled) {
          setOrders(readyOrders);
        }

        if (!isConfirmed && !cancelled) {
          setSelectedDailyIds(loadSelectedIds(LS_SELECTED_DAILY));
          setSelectedStoreIds(loadSelectedIds(LS_SELECTED_STORE));
          const legacy = loadSelectedIds("selectedOrderIds");
          if (legacy.length > 0) {
            const dailyIds: number[] = [];
            const storeIds: number[] = [];
            for (const id of legacy) {
              const order = readyOrders.find((o: { id?: number }) => o.id === id);
              if (!order) continue;
              if (String(order.order_type ?? "").toLowerCase() === "subscription") {
                dailyIds.push(id);
              } else {
                storeIds.push(id);
              }
            }
            if (dailyIds.length > 0) {
              setSelectedDailyIds((prev) =>
                prev.length ? prev : dailyIds
              );
            }
            if (storeIds.length > 0) {
              setSelectedStoreIds((prev) =>
                prev.length ? prev : storeIds
              );
            }
            localStorage.removeItem("selectedOrderIds");
          }
        }
      } catch (err) {
        console.error("Failed to fetch today's orders:", err);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setDataReady(true);
        }
      }
    };

    loadOrders();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [setLoading, isConfirmed, ordersRefreshKey]);

  useEffect(() => {
    saveSelectedIds(LS_SELECTED_DAILY, selectedDailyIds);
  }, [selectedDailyIds]);

  useEffect(() => {
    saveSelectedIds(LS_SELECTED_STORE, selectedStoreIds);
  }, [selectedStoreIds]);

  useEffect(() => {
    if (isConfirmed && !confirmTimeDaily && !confirmTimeStore) {
      const timeString = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      setConfirmTimeDaily(timeString);
      setConfirmTimeStore(timeString);
    }
  }, [isConfirmed, confirmTimeDaily, confirmTimeStore]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orders;

    const words = term.split(/\s+/).filter(Boolean);

    return orders.filter((order) => {
      const firstItem = order.items?.[0];
      const productName = firstItem?.product?.name || "";
      const customerName = order.delivery_address?.receiver_name || "";
      const phone = order.delivery_address?.receiver_phone || "";
      const orderNumber = order.order_number || order.id?.toString() || "";
      const address = order.delivery_address
        ? formatDeliveryAddressOrFallback(
            order.delivery_address as Record<string, unknown>
          )
        : "";

      const allProductNames = Array.isArray(order.items)
        ? order.items
            .map(
              (it: { product?: { name?: string } }) =>
                it?.product?.name || ""
            )
            .join(" ")
        : "";

      const searchable = [
        productName,
        allProductNames,
        customerName,
        phone.replace(/\s/g, ""),
        orderNumber,
        address,
      ]
        .join(" ")
        .toLowerCase();

      return words.every((w) => searchable.includes(w));
    });
  }, [orders, searchTerm]);

  const orderPartition = useMemo(() => {
    const part = partitionDailyAndStoreOrders(filteredOrders, (order) =>
      order as Record<string, unknown>
    );
    return {
      dailyOrders: sortBasketOrdersForDisplay(
        part.dailyOrders,
        (o) => o as Record<string, unknown>
      ),
      storeSlotEntries: part.storeSlotEntries.map(
        ([slot, list]) =>
          [slot, sortBasketOrdersForDisplay(list, (o) => o as Record<string, unknown>)] as [
            string,
            typeof list,
          ]
      ),
    };
  }, [filteredOrders]);

  const tabOrders = useMemo(() => {
    if (basketTab === "daily") return orderPartition.dailyOrders;
    return orderPartition.storeSlotEntries.flatMap(([, list]) => list);
  }, [basketTab, orderPartition]);

  const storeSlotKeys = useMemo(
    () => defaultStoreSlotExpandKeys(orderPartition),
    [orderPartition]
  );

  useEffect(() => {
    const { dailyOrders, storeSlotEntries } = orderPartition;
    if (dailyOrders.length === 0 && storeSlotEntries.length > 0) {
      setBasketTab("store");
    }
  }, [orderPartition]);

  useEffect(() => {
    if (basketTab === "store" && storeSlotKeys.length > 0) {
      setExpandedSlots(new Set(storeSlotKeys));
    }
  }, [basketTab, storeSlotKeys]);

  const toggleSlot = (slot: string) => {
    setExpandedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return next;
    });
  };

  const confirmableTabOrders = useMemo(
    () => tabOrders.filter((o) => isOrderConfirmableInBasket(o as Record<string, unknown>)),
    [tabOrders]
  );

  const toggleOrder = (orderId: number) => {
    const order = tabOrders.find((o) => o.id === orderId);
    if (order && !isOrderConfirmableInBasket(order as Record<string, unknown>)) {
      return;
    }
    setSelectedIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    const tabIds = confirmableTabOrders.map((o) => o.id);
    if (tabIds.length === 0) return;
    const allSelected = tabIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !tabIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...tabIds])]);
    }
  };

  const handleConfirm = async () => {
    if (selectedIds.length === 0) {
      setShowWarning(true);
      return;
    }

    if (partnerCheckedIn !== true) {
      onCheckInRequired?.();
      return;
    }

    const selectedOrders = orders.filter((order) =>
      selectedIds.includes(order.id)
    );
    const confirmableSelected = selectedOrders.filter((o) =>
      isOrderConfirmableInBasket(o as Record<string, unknown>)
    );
    const skippedPreparing = selectedOrders.length - confirmableSelected.length;

    if (confirmableSelected.length === 0) {
      showError(
        "Selected orders are still preparing at the store. Confirm pickup when status is Ready or Partner assigned."
      );
      return;
    }

    if (skippedPreparing > 0) {
      showError(
        `${skippedPreparing} order${skippedPreparing > 1 ? "s are" : " is"} still preparing and will be skipped.`
      );
    }

    setIsConfirming(true);

    try {
      const idsToPickup = confirmableSelected.map((o) => o.id);

      const pickupPromises = idsToPickup.map(async (orderId) => {
        const order = confirmableSelected.find((o) => o.id === orderId);
        const st = String(order?.status ?? "")
          .toLowerCase()
          .trim();
        try {
          if (st === "out_for_delivery") {
            const response = await pickupOrder(orderId);
            return {
              orderId,
              order: { ...order, pickupResponse: response },
              mode: "released" as const,
            };
          }
          const response = await pickupOrder(orderId);
          return {
            orderId,
            order: { ...order, status: "out_for_delivery", pickupResponse: response },
            mode: "pickup" as const,
          };
        } catch (error: unknown) {
          console.error(`Failed to pickup order ${orderId}:`, error);
          throw { orderId, error };
        }
      });

      const results = await Promise.allSettled(pickupPromises);

      const successfulPickups: { orderId: number }[] = [];
      const failedPickups: { orderId: number; error: unknown }[] = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successfulPickups.push(result.value);
        } else {
          failedPickups.push({
            orderId: idsToPickup[index],
            error: result.reason?.error || result.reason,
          });
        }
      });

      const releasedIds = successfulPickups
        .map((r) => r.orderId)
        .filter((id) => Number.isFinite(Number(id)));

      if (releasedIds.length > 0) {
        addReleasedOrderIds(releasedIds);
      }

      const tabLabel = basketTab === "daily" ? "Daily" : "Store";

      if (successfulPickups.length > 0) {
        if (failedPickups.length === 0) {
          showSuccess(
            `${tabLabel}: released ${successfulPickups.length} order${successfulPickups.length > 1 ? "s" : ""} to Today's deliveries.`
          );
        } else {
          showSuccess(
            `${tabLabel}: released ${successfulPickups.length} order${successfulPickups.length > 1 ? "s" : ""}. ${failedPickups.length} failed.`
          );
        }
      }

      if (failedPickups.length > 0) {
        const errorMessages = failedPickups.map((f) => {
          const order = selectedOrders.find((o) => o.id === f.orderId);
          return order?.order_number || `#${f.orderId}`;
        });
        showError(
          `${tabLabel}: could not complete ${failedPickups.length} order${failedPickups.length > 1 ? "s" : ""}: ${errorMessages.join(", ")}`
        );
      }

      if (successfulPickups.length === 0) {
        showError("No orders were released. Please try again.");
        setIsConfirming(false);
        return;
      }

      let remainingOtherTab = 0;
      const otherTab: DailyStoreTab =
        basketTab === "daily" ? "store" : "daily";

      try {
        const refresh = await fetchTodaysDeliveries();
        const all = refresh.orders || [];
        const releasedAfter = getReleasedOrderIdsSynced(
          all as Array<{ id?: number; status?: string }>
        );
        const basketOrders = all.filter((order: Record<string, unknown>) =>
          isOrderEligibleForBasket(order, releasedAfter)
        );
        setOrders(basketOrders);
        const part = partitionDailyAndStoreOrders(basketOrders, (o) => o);
        remainingOtherTab =
          otherTab === "daily"
            ? part.dailyOrders.length
            : part.storeSlotEntries.reduce((n, [, list]) => n + list.length, 0);
      } catch (e) {
        console.error("Basket refresh after confirm:", e);
      }

      setSelectedIds((prev) => prev.filter((id) => !releasedIds.includes(id)));

      const timeString = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      setConfirmTime(timeString);

      if (remainingOtherTab > 0) {
        setOtherTabReminder({ tab: otherTab, count: remainingOtherTab });
      } else {
        onConfirm();
      }
    } catch (error) {
      console.error("Failed to confirm orders:", error);
      showError("An error occurred while confirming orders. Please try again.");
    } finally {
      setIsConfirming(false);
    }
  };

  const totalPackQuantity = packQuantityForOrders(tabOrders);
  const categoryCounts = categoryCountsForOrders(
    tabOrders.filter((o) => selectedIds.includes(o.id))
  );

  const dailyCount = orderPartition.dailyOrders.length;
  const storeCount = orderPartition.storeSlotEntries.reduce(
    (n, [, list]) => n + list.length,
    0
  );

  const renderOrderCard = useCallback(
    (order: (typeof orders)[0]) => {
      const status = getOrderBasketStatus(order as Record<string, unknown>);
      const canConfirm = isOrderConfirmableInBasket(
        order as Record<string, unknown>
      );
      const isPreparing = status === "preparing";
      const isSelected = selectedIds.includes(order.id);
      const firstItem = order.items?.[0];
      const productName = firstItem?.product?.name || "Unnamed Pack";
      const productImage = pickPrimaryImageUrl(firstItem?.product, "thumb");
      const category = firstItem?.product?.category_name || "Puja Flowers";
      const quantity =
        order.items?.reduce(
          (sum: number, item: { quantity?: number }) =>
            sum + (item.quantity || 1),
          0
        ) || 1;
      const isPujaFlowers =
        category === "Puja Flowers" || category.includes("Pooja");
      const borderColor = isPujaFlowers
        ? "border-amber-300"
        : "border-purple-300";
      const bgColor = isPujaFlowers ? "bg-amber-50" : "bg-purple-50";
      const textColor = isPujaFlowers ? "text-amber-700" : "text-purple-700";
      const buttonBgColor = isSelected
        ? isPujaFlowers
          ? "bg-amber-500"
          : "bg-purple-500"
        : "bg-gray-100";

      const lineCount = Array.isArray(order.items) ? order.items.length : 0;
      const hasMultipleLines = lineCount > 1;

      return (
        <div
          key={order.id}
          className={`border rounded-lg p-3 ${
            isSelected ? `${borderColor} ${bgColor}` : "border-gray-200"
          } ${!canConfirm ? "opacity-80 bg-gray-50/80" : ""} ${isConfirmed ? "opacity-75" : ""}`}
        >
          <div
            className={`flex items-center justify-between ${
              canConfirm ? "cursor-default" : "cursor-not-allowed"
            }`}
            onClick={() => toggleOrder(order.id)}
          >
            <div className="flex items-center flex-1">
              {productImage && (
                <img
                  src={productImage}
                  alt={productName}
                  className="w-12 h-12 rounded-md object-cover mr-3"
                />
              )}
              <div className="flex-1">
                <h3 className={`font-medium ${textColor} text-sm`}>
                  {productName}
                  {hasMultipleLines ? (
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">
                      +{lineCount - 1} more item
                      {lineCount - 1 !== 1 ? "s" : ""}
                    </span>
                  ) : null}
                </h3>
                <p className="text-xs text-gray-500">
                  Order: {order.order_number || `#${order.id}`}
                </p>
                <p className="text-xs text-gray-500">
                  {order.delivery_address?.receiver_name || "Customer"}
                </p>
                {isPreparing && (
                  <p className="text-xs text-amber-700 font-medium mt-1">
                    Preparing at store — confirm when ready
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold ${textColor}`}>
                {quantity}x
              </span>
              <button
                type="button"
                className={`p-2 rounded-full ${
                  canConfirm ? `${buttonBgColor} text-white cursor-pointer` : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
                disabled={isConfirmed || !canConfirm}
                aria-label={
                  !canConfirm
                    ? "Not ready for pickup"
                    : isSelected
                      ? "Deselect order"
                      : "Select order"
                }
              >
                <CheckCircleIcon size={18} />
              </button>
            </div>
          </div>
          <button
            type="button"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg border font-semibold transition-colors active:scale-[0.99] text-sm py-2.5 px-4 border-gray-200 bg-white text-gray-800 hover:bg-gray-50 w-max max-w-full justify-start text-left cursor-pointer min-h-[44px]"
            onClick={(e) => {
              e.stopPropagation();
              setDetailsOrder(order as OrderLikeForDetails);
            }}
          >
            <ListIcon
              size={18}
              className="flex-shrink-0 text-gray-500"
              aria-hidden
            />
            <span className="truncate">
              View order details
              {lineCount > 1 ? (
                <span className="text-gray-500 font-normal">
                  {" "}
                  ({lineCount})
                </span>
              ) : null}
            </span>
          </button>
        </div>
      );
    },
    [isConfirmed, selectedIds, toggleOrder]
  );

  if (!dataReady) {
    return <div className={MAIN_LOADER_MIN_HEIGHT} aria-hidden />;
  }

  const tabConfirmLabel = basketTab === "daily" ? "Daily" : "Store";
  const lastConfirmForTab =
    basketTab === "daily" ? confirmTimeDaily : confirmTimeStore;

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold text-gray-800 mb-2">My Basket</h2>
      <p className="text-gray-600 mb-4">
        Check all items before starting deliveries
      </p>

      <DailyStoreTabs
        className="mb-4"
        activeTab={basketTab}
        onTabChange={setBasketTab}
        dailyCount={dailyCount}
        storeCount={storeCount}
      />

      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ShoppingBasketIcon size={20} className="text-blue-600 mr-2" />
            <span className="font-medium text-gray-800">
              {basketTab === "daily" ? "Daily subscriptions" : "Store orders"}
            </span>
          </div>
          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm font-medium">
            {totalPackQuantity} pack{totalPackQuantity !== 1 ? "s" : ""}
          </span>
        </div>

        {tabOrders.length > 0 && (
          <div className="mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={confirmableTabOrders.length === 0}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {confirmableTabOrders.length > 0 &&
                confirmableTabOrders.every((o) => selectedIds.includes(o.id))
                  ? "Deselect all"
                  : "Select ready"}
              </button>
              <span className="text-sm text-gray-600">
                {selectedIds.filter((id) =>
                  confirmableTabOrders.some((o) => o.id === id)
                ).length}{" "}
                of {confirmableTabOrders.length} ready
                {tabOrders.length > confirmableTabOrders.length
                  ? ` (${tabOrders.length - confirmableTabOrders.length} preparing)`
                  : ""}
              </span>
            </div>
          </div>
        )}

        {Object.keys(categoryCounts).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-100 pb-3">
            {Object.entries(categoryCounts).map(([category, count], index) => (
              <div
                key={index}
                className={
                  category === "Puja Flowers" || category.includes("Pooja")
                    ? "bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-sm"
                    : "bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-sm"
                }
              >
                {category}: <span className="font-bold">{count}</span>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          {tabOrders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ShoppingBasketIcon
                size={48}
                className="mx-auto mb-2 opacity-50"
              />
              <p>
                No {basketTab === "daily" ? "daily" : "store"} orders in basket
              </p>
            </div>
          ) : basketTab === "daily" ? (
            <DeliveryOrderGroupSections
              partition={orderPartition}
              sections="daily"
              expandedSlots={expandedSlots}
              onToggleSlot={toggleSlot}
              renderOrder={renderOrderCard}
            />
          ) : (
            <DeliveryOrderGroupSections
              partition={orderPartition}
              sections="store"
              expandedSlots={expandedSlots}
              onToggleSlot={toggleSlot}
              renderOrder={renderOrderCard}
            />
          )}
        </div>
      </div>

      <div className="mt-4 text-center">
        {lastConfirmForTab && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-3">
            <div className="flex items-center justify-center text-green-700 mb-2">
              <CheckCircleIcon size={20} className="mr-2" />
              <span className="font-medium">
                Last {tabConfirmLabel} batch confirmed
              </span>
            </div>
            <div className="flex items-center justify-center text-sm text-gray-600">
              <ClockIcon size={16} className="mr-1" />
              <span>at {lastConfirmForTab}</span>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={
            tabOrders.length === 0 ||
            selectedIds.length === 0 ||
            isConfirming
          }
          className={`px-6 py-3 rounded-md text-white font-medium ${
            tabOrders.length === 0 || selectedIds.length === 0
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600"
          }`}
        >
          {isConfirming
            ? "Confirming..."
            : tabOrders.length === 0
              ? `No ${tabConfirmLabel.toLowerCase()} items`
              : selectedIds.length === 0
                ? `Select ${tabConfirmLabel.toLowerCase()} items`
                : `Confirm ${selectedIds.length} ${tabConfirmLabel.toLowerCase()} item${selectedIds.length > 1 ? "s" : ""}`}
        </button>
      </div>

      <OrderDetailsModal
        isOpen={detailsOrder !== null}
        onClose={() => setDetailsOrder(null)}
        order={detailsOrder}
      />

      {otherTabReminder && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setOtherTabReminder(null)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              More orders in {otherTabReminder.tab === "daily" ? "Daily" : "Store"}
            </h3>
            <p className="mb-4 text-sm text-gray-600">
              You still have {otherTabReminder.count} order
              {otherTabReminder.count !== 1 ? "s" : ""} in the{" "}
              <strong>
                {otherTabReminder.tab === "daily" ? "Daily" : "Store"}
              </strong>{" "}
              tab. Please review and confirm those before starting deliveries.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setBasketTab(otherTabReminder.tab);
                  setOtherTabReminder(null);
                }}
                className="w-full px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-medium"
              >
                Go to {otherTabReminder.tab === "daily" ? "Daily" : "Store"} tab
              </button>
              <button
                type="button"
                onClick={() => setOtherTabReminder(null)}
                className="w-full px-4 py-2.5 border border-gray-200 text-gray-700 rounded-md"
              >
                Stay here
              </button>
            </div>
          </div>
        </div>
      )}

      {showWarning && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowWarning(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium text-amber-600 mb-2">
              Check all items
            </h3>
            <p className="mb-4">
              Please select at least one {basketTab} order before confirming.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowWarning(false)}
                className="px-4 py-2 bg-amber-600 text-white rounded-md"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBasket;
